import { useCallback, useEffect } from 'react'
import { toast } from 'react-toastify'

import { RequestStatus } from 'core/domain/Request'
import { intSizes } from 'core/miranda-styles/screen'
import useWindowWidth from 'core/ui/hooks/useWindowWidth'

import { useConfigContext } from 'config/ui/ConfigContext'

import { analytics } from '../../../core/utils/analytics'
import { type AgnosticShipmentFilter } from '../../domain/AgnosticShipmentFilter'
import { useGetShipmentsMetadata } from '../hooks/useGetShipmentsMetadata'
import { type SearchFilters } from '../ShipperLoadsPage'
import { ShipmentsMetadataDesktopView } from './components/ShipmentsMetadataDesktopView'
import { ShipmentsMetadataMobileView } from './components/ShipmentsMetadataMobileView'

type ShipmentsMetadataProps = {
  filters: AgnosticShipmentFilter
  onClick: (filters: SearchFilters | undefined) => void
  shouldRenderIfMobile?: boolean
  useGetShipmentsMetadata?: typeof useGetShipmentsMetadata
}

export type FilterKey = 'pendingAppointments' | 'missingPods' | 'missingInvoices'
export type FilterEntrypoint = 'summaryCard' | 'savedFiltersDropdown'

export function ShipmentsMetadata({
  filters,
  onClick,
  shouldRenderIfMobile,
  useGetShipmentsMetadata: useShipmentsMetadata = useGetShipmentsMetadata,
}: ShipmentsMetadataProps) {
  const windowWidth = useWindowWidth()
  const isMobile = windowWidth <= intSizes.sm
  const deviceType = isMobile ? 'mobile' : 'desktop'

  const configs = useConfigContext()
  const isConfigsLoaded = configs.configStatus.isDone
  const enableShipmentsMetadata = configs.enableShipmentsMetadata.toBoolean()

  const { shipmentsMetadata, status, fetchShipmentsMetadata } = useShipmentsMetadata()
  const renderMobile = isMobile && shouldRenderIfMobile
  const renderDesktop = !isMobile && !shouldRenderIfMobile
  const shouldFetchMetadata = renderMobile || renderDesktop

  useEffect(() => {
    if (isConfigsLoaded && shouldFetchMetadata && enableShipmentsMetadata) {
      fetchShipmentsMetadata()
    }
  }, [enableShipmentsMetadata, fetchShipmentsMetadata, isConfigsLoaded, shouldFetchMetadata])

  useEffect(() => {
    if (status === RequestStatus.Error) {
      toast.error('Failed to fetch shipments metadata')
    }
  }, [status])

  const handleFilterClick = useCallback(
    (filterKey?: FilterKey, entrypoint: FilterEntrypoint = 'summaryCard') => {
      if (!filterKey) {
        onClick({
          pendingAppointments: undefined,
          missingPods: undefined,
          missingInvoices: undefined,
        })
        return
      }

      const isActive = filters[filterKey]

      if (isActive) {
        onClick({ [filterKey]: undefined })
        return
      }

      analytics.track('Clicked: Big number', {
        filterName: filterKey,
        entrypoint,
        deviceType,
      })

      const allFilters: Record<FilterKey, boolean | undefined> = {
        pendingAppointments: undefined,
        missingPods: undefined,
        missingInvoices: undefined,
      }

      allFilters[filterKey] = true
      onClick(allFilters)
    },
    [deviceType, filters, onClick]
  )

  if (!enableShipmentsMetadata) {
    return null
  }

  const filterKeys: Array<FilterKey> = ['pendingAppointments', 'missingPods', 'missingInvoices']
  const selectedFilter = filterKeys.find(key => filters[key])
  const totalShipments = Number(shipmentsMetadata?.total ?? 0)

  if (renderMobile) {
    return (
      <ShipmentsMetadataMobileView
        selectedFilter={selectedFilter}
        onFilterClick={handleFilterClick}
      />
    )
  }

  if (renderDesktop) {
    return (
      <ShipmentsMetadataDesktopView
        filters={filters}
        shipmentsMetadata={shipmentsMetadata}
        totalShipments={totalShipments}
        onFilterClick={handleFilterClick}
      />
    )
  }

  return null
}
